import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Upload, X } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { generateWeldNdtPDF } from '@/services/weldNdtPDFTemplate'
import {
  WELD_NDT_MATERIALS,
  WELD_NDT_TEST_SITES,
  createDefaultWeldNdtFormData,
} from '@/types/weldNdtTypes'
import type { WeldNdtFormData, WeldNdtMethodConfig, WeldNdtMaterialType } from '@/types/weldNdtTypes'
import { generateOrderNumber, getNextMethodReportSequence } from '@/utils/getNextReportSequence'
import { getNdtExitPath } from '@/utils/ndtNavigation'
import { applySelectedEquipmentToForm, attachNdtReportToEquipment } from '@/utils/ndtEquipmentReport'
import { useNdtPersonnel } from '@/hooks/useNdtPersonnel'
import NdtFormPage, {
  NdtFormActions,
  NdtStatusButtons,
  ndtInputCls,
  ndtLabelCls,
  ndtSelectCls,
  ndtTextareaCls,
  ndtUploadCls,
  ndtPrimaryBtnCls,
  ndtSecondaryBtnCls,
} from '@/components/ndt/NdtFormPage'
import NdtEquipmentSearch, { type NdtEquipmentHit } from '@/components/ndt/NdtEquipmentSearch'

interface WeldNdtTestFormProps {
  config: WeldNdtMethodConfig
}

const WeldNdtTestForm = ({ config }: WeldNdtTestFormProps) => {
  const navigate = useNavigate()
  const { inspectors, admins, companies, currentUser, loading, isAdmin, isInspectorLocked } = useNdtPersonnel()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null)
  const [formData, setFormData] = useState<WeldNdtFormData>(() => createDefaultWeldNdtFormData(config))

  useEffect(() => {
    if (isInspectorLocked && currentUser?.full_name && !formData.issuedBy) {
      setFormData((prev) => ({ ...prev, issuedBy: currentUser.full_name }))
    }
  }, [isInspectorLocked, currentUser, formData.issuedBy])

  const canSubmit = useMemo(() => {
    if (!formData.reportNumber.trim() || !formData.issuedBy.trim()) return false
    if (!formData.objectDescription.trim()) return false
    if (formData.objects.every((obj) => !obj.trim())) return false
    return true
  }, [formData])

  const selectedCompanyName = useMemo(
    () => companies.find((c) => c.id === formData.company)?.name ?? '',
    [companies, formData.company],
  )

  const handleSelectEquipment = (equipment: NdtEquipmentHit | null) => {
    setSelectedEquipmentId(equipment?.id ?? null)
    if (!equipment) return
    setFormData((prev) => applySelectedEquipmentToForm(prev, equipment))
  }

  const handleCompanyChange = (companyId: string) => {
    const selected = companies.find((c) => c.id === companyId)
    setSelectedEquipmentId(null)
    if (!selected) {
      setFormData((prev) => ({ ...prev, company: '', report_code: null }))
      return
    }
    setFormData((prev) => ({
      ...prev,
      company: companyId,
      report_code: selected.report_code,
      reportNumber: selected.report_code ? generateOrderNumber(selected.report_code) : prev.reportNumber,
    }))
  }

  const buildPayload = useCallback(
    (sequenceNumber: string) => ({
      ...formData,
      objects: formData.objects.filter((obj) => obj.trim()),
      certificationNumber: '---',
      sequenceNumber,
    }),
    [formData],
  )

  const handlePreview = async () => {
    setIsGeneratingPreview(true)
    try {
      const { sequenceNumber } = await getNextMethodReportSequence(config.method, formData.reportNumber)
      const blob = await generateWeldNdtPDF(buildPayload(sequenceNumber), config)
      setPreviewUrl(URL.createObjectURL(blob))
    } catch (error) {
      console.error(error)
      alert('Kunne ikke generere forhåndsvisning.')
    } finally {
      setIsGeneratingPreview(false)
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!canSubmit) {
      alert('Vennligst fyll ut alle påkrevde felt.')
      return
    }
    setIsSubmitting(true)
    try {
      const { storageFolder, sequenceNumber, reportId } = await getNextMethodReportSequence(
        config.method,
        formData.reportNumber,
      )
      const folderPath = `${storageFolder}/${reportId}.pdf`
      const filteredObjects = formData.objects.filter((obj) => obj.trim())
      const pdfBlob = await generateWeldNdtPDF(buildPayload(sequenceNumber), config)

      const { error: uploadError } = await supabase.storage
        .from('reports')
        .upload(folderPath, pdfBlob, { contentType: 'application/pdf', upsert: true })
      if (uploadError) throw uploadError

      const { data: { user } } = await supabase.auth.getUser()
      const { attachedImage: _omit, ...stored } = formData
      const insertData: Record<string, unknown> = {
        order_number: reportId,
        unit_id: filteredObjects.join(', ') || formData.objectDescription,
        test_date: formData.testDate,
        test_type: config.method,
        status: formData.status,
        issued_by: formData.issuedBy,
        verified_by: formData.verifiedBy?.trim() || null,
        file_path: folderPath,
        report_code: formData.report_code,
        created_by: user?.id,
        report_data: { ...stored, objects: filteredObjects, sequenceNumber, equipmentId: selectedEquipmentId },
      }
      if (formData.company?.trim()) insertData.company_id = formData.company
      if (selectedEquipmentId) insertData.equipment_id = selectedEquipmentId

      const { error: metadataError } = await supabase.from('ndt_reports').insert(insertData)
      if (metadataError) throw metadataError

      if (selectedEquipmentId) {
        await attachNdtReportToEquipment({
          pdfBlob,
          fileName: `${reportId}.pdf`,
          customerName: selectedCompanyName,
          serialNumber: filteredObjects[0] || formData.objectDescription || reportId,
          equipmentId: selectedEquipmentId,
          testDate: formData.testDate,
          reportStatus: formData.status,
        })
      }

      const { data: signed } = await supabase.storage.from('reports').createSignedUrl(folderPath, 60)
      if (signed?.signedUrl) window.open(signed.signedUrl, '_blank')

      alert(selectedEquipmentId
        ? 'Rapport generert, lagret på utstyr og NDT-datoer oppdatert.'
        : 'Rapport generert og lagret.')
      navigate(getNdtExitPath())
    } catch (error) {
      console.error(error)
      alert(error instanceof Error ? error.message : 'Feil ved generering av rapport. Prøv igjen.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = () => setFormData((prev) => ({ ...prev, attachedImage: reader.result as string }))
    reader.readAsDataURL(file)
  }

  return (
    <NdtFormPage
      highlight={config.highlight}
      title={config.formTitle}
      loading={loading}
      previewUrl={previewUrl}
      previewTitle={config.previewTitle}
      onClosePreview={() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }}
      onPublish={() => void handleSubmit()}
      isPublishing={isSubmitting}
      canPublish={canSubmit}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Testdato</label>
            <input type="date" required value={formData.testDate} onChange={(e) => setFormData((p) => ({ ...p, testDate: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Teststed</label>
            <select value={formData.testSite} onChange={(e) => setFormData((p) => ({ ...p, testSite: e.target.value }))} className={ndtSelectCls}>
              {WELD_NDT_TEST_SITES.map((site) => (
                <option key={site} value={site}>{site}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Kunde</label>
            <select value={formData.company || ''} onChange={(e) => handleCompanyChange(e.target.value)} className={ndtSelectCls}>
              <option value="">Velg kunde</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={ndtLabelCls}>Vårt ordrenummer</label>
            <input type="text" required value={formData.reportNumber} onChange={(e) => setFormData((p) => ({ ...p, reportNumber: e.target.value }))} className={ndtInputCls} placeholder="STM ordrenummer" />
          </div>
        </div>

        <NdtEquipmentSearch
          key={formData.company || 'none'}
          companyId={formData.company || ''}
          onSelect={handleSelectEquipment}
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Objektbeskrivelse</label>
            <input type="text" required value={formData.objectDescription} onChange={(e) => setFormData((p) => ({ ...p, objectDescription: e.target.value }))} className={ndtInputCls} placeholder="Beskriv objektet" />
          </div>
          <div>
            <label className={ndtLabelCls}>Objekt-ID</label>
            {formData.objects.map((obj, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input type="text" value={obj} onChange={(e) => {
                  const objects = [...formData.objects]
                  objects[index] = e.target.value
                  setFormData((p) => ({ ...p, objects }))
                }} placeholder={`Objekt ${index + 1}`} className={`${ndtInputCls} flex-1`} />
                {formData.objects.length > 1 && (
                  <button type="button" onClick={() => setFormData((p) => ({ ...p, objects: p.objects.filter((_, i) => i !== index) }))} className="p-2 text-red-600 hover:bg-red-50 rounded-md">
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setFormData((p) => ({ ...p, objects: [...p.objects, ''] }))} className="inline-flex items-center gap-2 text-sm text-[#0D9488] font-medium min-h-[44px]">
              <Plus size={18} /> Legg til objekt
            </button>
          </div>
        </div>

        <div>
          <label className={ndtLabelCls}>Materiale</label>
          <select value={formData.material} onChange={(e) => setFormData((p) => ({ ...p, material: e.target.value as WeldNdtMaterialType }))} className={ndtSelectCls}>
            {WELD_NDT_MATERIALS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={ndtLabelCls}>Tegning</label>
            <input type="text" value={formData.drawing} onChange={(e) => setFormData((p) => ({ ...p, drawing: e.target.value }))} className={ndtInputCls} placeholder="Tegningsnr." />
          </div>
          <div>
            <label className={ndtLabelCls}>Diameter</label>
            <input type="text" value={formData.diameter} onChange={(e) => setFormData((p) => ({ ...p, diameter: e.target.value }))} className={ndtInputCls} placeholder="f.eks. Ø114.3" />
          </div>
          <div>
            <label className={ndtLabelCls}>Tykkelse</label>
            <input type="text" value={formData.thickness} onChange={(e) => setFormData((p) => ({ ...p, thickness: e.target.value }))} className={ndtInputCls} placeholder="f.eks. 8.0 mm" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Sveiser</label>
            <input type="text" value={formData.welder} onChange={(e) => setFormData((p) => ({ ...p, welder: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Sveiseprosedyre</label>
            <input type="text" value={formData.weldingProcedure} onChange={(e) => setFormData((p) => ({ ...p, weldingProcedure: e.target.value }))} className={ndtInputCls} placeholder="WPS" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Fuge / Joint</label>
            <input type="text" value={formData.joint} onChange={(e) => setFormData((p) => ({ ...p, joint: e.target.value }))} className={ndtInputCls} placeholder="Butt, Fillet, T-joint" />
          </div>
          <div>
            <label className={ndtLabelCls}>PWHT</label>
            <input type="text" value={formData.pwht} onChange={(e) => setFormData((p) => ({ ...p, pwht: e.target.value }))} className={ndtInputCls} placeholder="Yes / No" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Tilsettmateriale</label>
            <input type="text" value={formData.fillerMet} onChange={(e) => setFormData((p) => ({ ...p, fillerMet: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Sveisemetode</label>
            <input type="text" value={formData.weldingMethod} onChange={(e) => setFormData((p) => ({ ...p, weldingMethod: e.target.value }))} className={ndtInputCls} placeholder="MMA, MAG, TIG" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Overflate</label>
            <input type="text" value={formData.surfaceCondition} onChange={(e) => setFormData((p) => ({ ...p, surfaceCondition: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Objekttemperatur</label>
            <input type="text" value={formData.temperatureOfObject} onChange={(e) => setFormData((p) => ({ ...p, temperatureOfObject: e.target.value }))} className={ndtInputCls} />
          </div>
        </div>

        <div>
          <label className={ndtLabelCls}>Testprosedyre</label>
          <select value={formData.testProcedure} onChange={(e) => setFormData((p) => ({ ...p, testProcedure: e.target.value }))} className={ndtSelectCls}>
            {config.procedureOptions.map((procedure) => (
              <option key={procedure} value={procedure}>{procedure}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Akseptansekriterium</label>
            <input type="text" value={formData.testAcceptance} onChange={(e) => setFormData((p) => ({ ...p, testAcceptance: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Omfang (%)</label>
            <input type="text" value={formData.extentOfExamination} onChange={(e) => setFormData((p) => ({ ...p, extentOfExamination: e.target.value }))} className={ndtInputCls} />
          </div>
        </div>

        <div>
          <label className={ndtLabelCls}>Referanse til regelverk og standarder</label>
          <input type="text" value={formData.referenceStandards} onChange={(e) => setFormData((p) => ({ ...p, referenceStandards: e.target.value }))} className={ndtInputCls} />
        </div>

        <div>
          <label className={ndtLabelCls}>Indikasjoner / antall reparasjoner</label>
          <textarea rows={3} value={formData.indications} onChange={(e) => setFormData((p) => ({ ...p, indications: e.target.value }))} className={ndtTextareaCls} placeholder="None" />
        </div>

        {isAdmin && (
          <div>
            <label className={ndtLabelCls}>Utført av</label>
            <select
              required
              value={formData.issuedBy}
              onChange={(e) => setFormData((p) => ({ ...p, issuedBy: e.target.value }))}
              className={ndtSelectCls}
            >
              <option value="">Velg inspektør</option>
              {inspectors.map((i) => (
                <option key={i.id} value={i.full_name}>{i.full_name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className={ndtLabelCls}>Ordreansvarlig</label>
          <select value={formData.verifiedBy || ''} onChange={(e) => setFormData((p) => ({ ...p, verifiedBy: e.target.value }))} className={ndtSelectCls}>
            <option value="">Ingen</option>
            {admins.map((a) => (
              <option key={a.id} value={a.full_name}>{a.full_name}</option>
            ))}
          </select>
        </div>

        <NdtStatusButtons
          value={formData.status}
          onChange={(status) => setFormData((p) => ({ ...p, status }))}
        />

        {(formData.status === 'Not Accepted' || formData.comments) && (
          <div>
            <label className={ndtLabelCls}>Kommentarer</label>
            <textarea rows={4} value={formData.comments || ''} onChange={(e) => setFormData((p) => ({ ...p, comments: e.target.value }))} className={ndtTextareaCls} />
          </div>
        )}

        <div>
          <label className={ndtLabelCls}>Bilde (valgfritt)</label>
          {!formData.attachedImage ? (
            <label className={ndtUploadCls}>
              <Upload size={16} className="text-slate-400" />
              <span className="text-sm text-slate-600">Last opp bilde</span>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          ) : (
            <div className="relative">
              <img src={formData.attachedImage} alt="Vedlegg" className="w-full h-64 object-cover rounded-md border border-slate-200" />
              <button type="button" onClick={() => setFormData((p) => ({ ...p, attachedImage: undefined }))} className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full">
                <X size={20} />
              </button>
            </div>
          )}
        </div>

        <NdtFormActions>
          <button type="button" onClick={() => void handlePreview()} disabled={isSubmitting || isGeneratingPreview} className={ndtSecondaryBtnCls}>
            {isGeneratingPreview ? 'Genererer…' : 'Forhåndsvis'}
          </button>
          <button type="submit" disabled={isSubmitting || !canSubmit} className={ndtPrimaryBtnCls}>
            {isSubmitting ? 'Genererer rapport...' : config.submitLabel}
          </button>
        </NdtFormActions>
      </form>
    </NdtFormPage>
  )
}

export default WeldNdtTestForm
